import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
  Slide,
  Tab,
  Tabs,
  Toolbar,
  useMediaQuery,
  useScrollTrigger,
} from "@material-ui/core";
import {
    Event as EventIcon,
    EventAvailable as EventAvailableIcon,
    List as ListIcon,
    NewReleases as NewReleasesIcon,
} from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import "./NavigationBar.scss";
import SettingsMenu from "../SettingsMenu";
import TeamSwitcher from "../TeamSwitcher";
import { UpcomingEventsContext } from "../../contexts/UpcomingEventsContext";
import { SongProposalsContext } from "../../contexts/SongProposalsContext";
import { UserContext } from "../../contexts/UserContext";

function HideOnScroll(props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({ target: window ? window() : undefined });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const routeTable = {
  "/login": 0,
  "/song-proposals": 0,
  "/upcoming": 1,
  "/calendar": 2,
  "/catalog": 3,
  "/setlist-templates": 4,
};

const useStyles = makeStyles((theme) => ({
  noHeightConstraint: {
    minHeight: "unset",
    fontSize: "12px",
  },
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
  },
  // TeamSwitcher renders nothing for most users (single-team), so its
  // own margin can't be what pushes this group to the far right - a
  // single auto margin on the group itself works whichever of its two
  // children are actually present.
  headerActions: {
    display: "flex",
    alignItems: "center",
    marginLeft: "auto",
  },
}));

export default () => {
  const location = useLocation();
  // routeTable has no entry for plenty of real pages (/events/*,
  // /songs/*, /admin, /account) - `false` (not `undefined`) is MUI
  // Tabs' own documented way to say "no tab selected", the one value it
  // doesn't warn about not matching a child. Re-synced on every
  // navigation, not just set once at mount: Navbar/PrivateRoute (see
  // App.js) never remounts between routes - it's the same PrivateRoute
  // component type match at every one of them - so without this,
  // arriving somewhere via anything other than clicking a Tab (a Link,
  // history.replace, a refresh landing on a non-tab page followed by an
  // in-app navigation) left `value` stale or, for a not-in-the-table
  // page, undefined - which is what MUI actually warns about.
  const [value, setValue] = React.useState(routeTable[location.pathname] ?? false);
  React.useEffect(() => {
    setValue(routeTable[location.pathname] ?? false);
  }, [location.pathname]);
  const { newCount } = React.useContext(UpcomingEventsContext);
  const { newCount: newProposalsCount } = React.useContext(SongProposalsContext);
  const { user } = React.useContext(UserContext);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const classes = useStyles();
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up("md"));

  const horizontalNavigationBar = (
    <Toolbar className={classes.noHeightConstraint}>
      <Tabs
        value={value}
        indicatorColor="secondary"
        textColor="secondary"
        onChange={handleChange}
        aria-label="page tabs"
      >
        <Tab
          label={
            <Badge
              badgeContent={newProposalsCount}
              color="secondary"
              invisible={!newProposalsCount}
              overlap="rectangular"
            >
              Song Proposals
            </Badge>
          }
          component={Link}
          to="/song-proposals"
          data-tour="nav-proposals"
        />
        <Tab
          label={
            <Badge
              badgeContent={newCount}
              color="secondary"
              invisible={!newCount}
              overlap="rectangular"
            >
              Upcoming
            </Badge>
          }
          component={Link}
          to="/upcoming"
          data-tour="nav-upcoming"
        />
        <Tab label="Calendar" component={Link} to="/calendar" data-tour="nav-calendar" />
        <Tab label="Catalog" component={Link} to="/catalog" data-tour="nav-catalog" />
        {user.is_team_leader && (
          <Tab
            label="Setlist Templates"
            component={Link}
            to={{ pathname: "/setlist-templates", state: { from: location } }}
            data-tour="nav-templates"
          />
        )}
      </Tabs>
      <Box className={classes.headerActions}>
        <TeamSwitcher />
        <SettingsMenu />
      </Box>
    </Toolbar>
  );

  // Mobile: primary navigation moves to a bottom tab bar (the
  // recognizable native-app pattern), leaving just the settings menu
  // in a slim top bar - no more hamburger/side drawer.
  const mobileTopBar = (
    <Toolbar className={classes.noHeightConstraint}>
      <Box className={classes.headerActions}>
        <TeamSwitcher />
        <SettingsMenu />
      </Box>
    </Toolbar>
  );

  const mobileBottomNav = (
    <Paper elevation={3} className={classes.bottomNav} square>
      <BottomNavigation value={value} onChange={handleChange} showLabels>
        <BottomNavigationAction
          label="Proposals"
          icon={
            <Badge
              badgeContent={newProposalsCount}
              color="secondary"
              invisible={!newProposalsCount}
              overlap="rectangular"
            >
              <NewReleasesIcon />
            </Badge>
          }
          component={Link}
          to="/song-proposals"
          data-tour="nav-proposals"
        />
        <BottomNavigationAction
          label="Upcoming"
          icon={
            <Badge
              badgeContent={newCount}
              color="secondary"
              invisible={!newCount}
              overlap="rectangular"
            >
              <EventAvailableIcon />
            </Badge>
          }
          component={Link}
          to="/upcoming"
          data-tour="nav-upcoming"
        />
        <BottomNavigationAction
          label="Calendar"
          icon={<EventIcon />}
          component={Link}
          to="/calendar"
          data-tour="nav-calendar"
        />
        <BottomNavigationAction
          label="Catalog"
          icon={<ListIcon />}
          component={Link}
          to="/catalog"
          data-tour="nav-catalog"
        />
      </BottomNavigation>
    </Paper>
  );

  // Event detail (/events/:id) and song detail (/songs/:id) are
  // full-screen pages with their own back-button app bar on both
  // breakpoints - showing the main chrome on top of/around that defeats
  // the point of it being full-screen, and neither is "on" any of the
  // bottom tabs anyway. Setlist template management needs the same
  // treatment only on mobile, which has no bottom-tab entry for it -
  // desktop already has its own persistent tab (see
  // horizontalNavigationBar).
  const isFullScreenPage =
    location.pathname.startsWith("/events/") ||
    location.pathname.startsWith("/songs/") ||
    (!largeScreen && location.pathname.startsWith("/setlist-templates"));

  const hideTopBar = location.pathname === "/login" || isFullScreenPage;
  const hideBottomNav = location.pathname === "/login" || isFullScreenPage;

  return (
    <div>
      {!hideTopBar && (
        <HideOnScroll>
          <AppBar color="inherit">
            {largeScreen ? horizontalNavigationBar : mobileTopBar}
          </AppBar>
        </HideOnScroll>
      )}
      {!largeScreen && !hideBottomNav && mobileBottomNav}
    </div>
  );
};
